import React, { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, format, isSameMonth, isSameDay, isWithinInterval, startOfDay, isBefore } from "date-fns";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const PRESETS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 days" },
  { id: "4w", label: "Last 4 weeks" },
  { id: "mtd", label: "Month to date" },
  { id: "qtd", label: "Quarter to date" },
  { id: "ytd", label: "Year to date" },
  { id: "all", label: "All time" },
];

export function presetRange(id, allStart, now = new Date()) {
  const end = startOfDay(now);
  const d = (n) => { const x = new Date(end); x.setDate(x.getDate() - n); return x; };
  switch (id) {
    case "today": return { start: end, end, label: "Today" };
    case "7d": return { start: d(6), end, label: "Last 7 days" };
    case "4w": return { start: d(27), end, label: "Last 4 weeks" };
    case "mtd": return { start: startOfMonth(end), end, label: "Month to date" };
    case "qtd": { const q = Math.floor(end.getMonth() / 3) * 3; return { start: new Date(end.getFullYear(), q, 1), end, label: "Quarter to date" }; }
    case "ytd": return { start: new Date(end.getFullYear(), 0, 1), end, label: "Year to date" };
    default: return { start: allStart, end, label: "All time" };
  }
}

function Month({ month, start, end, onPick, maxDate }) {
  const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(month)), end: endOfWeek(endOfMonth(month)) });
  return (
    <div className="min-w-[236px]">
      <div className="text-center text-compact font-semibold text-ink-primary mb-2">{format(month, "MMMM yyyy")}</div>
      <div className="grid grid-cols-7 text-micro text-ink-muted mb-1">{["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <div key={d} className="h-6 grid place-items-center">{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {days.map((day) => {
          const inMonth = isSameMonth(day, month);
          const isStart = start && isSameDay(day, start);
          const isEnd = end && isSameDay(day, end);
          const inRange = start && end && isWithinInterval(day, { start, end });
          const disabled = maxDate && isBefore(startOfDay(maxDate), startOfDay(day));
          return (
            <button key={day.toISOString()} onClick={() => !disabled && onPick(day)} disabled={disabled} data-testid={`day-${format(day, "yyyy-MM-dd")}`}
              className={cn("h-7 w-full grid place-items-center text-compact font-mono transition-colors",
                !inMonth && "text-ink-muted/40",
                disabled && "text-ink-muted/30 cursor-not-allowed",
                inRange && !isStart && !isEnd && "bg-emerald-100 text-emerald-700",
                (isStart || isEnd) ? "bg-emerald-600 text-white rounded-md" : !disabled && "rounded-md hover:bg-surface-2")}>
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DateRangePopover({ value, allStart, onApply, trigger, align = "start", maxDate }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(startOfMonth(value?.start || new Date()));
  const [start, setStart] = useState(value?.start || null);
  const [end, setEnd] = useState(value?.end || null);

  const pick = (day) => {
    if (!start || (start && end)) { setStart(day); setEnd(null); }
    else if (isBefore(day, start)) { setEnd(start); setStart(day); }
    else setEnd(day);
  };
  const applyPreset = (id) => { const r = presetRange(id, allStart); setStart(r.start); setEnd(r.end); onApply(r); setOpen(false); };
  const apply = () => { if (start && end) { onApply({ start, end, label: `${format(start, "MMM d, yyyy")} – ${format(end, "MMM d, yyyy")}` }); setOpen(false); } };
  const clear = () => { setStart(null); setEnd(null); };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        {trigger || (
          <button data-testid="date-range-trigger" className="inline-flex items-center gap-2 h-9 px-3 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2 ez-press">
            <CalendarDays className="h-4 w-4 text-ink-muted" />
            <span className="text-ink-muted">Date range</span>
            <span className="font-medium text-ink-primary">{value?.label || "All time"}</span>
          </button>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align={align} sideOffset={8} data-testid="date-range-popover"
          className="z-[150] bg-surface-0 border border-edge-subtle rounded-panel shadow-menu p-4 ez-rise-in max-w-[92vw]">
          <div className="flex gap-4">
            <div className="hidden sm:flex flex-col gap-0.5 pr-3 border-r border-edge-subtle">
              {PRESETS.map((p) => (
                <button key={p.id} data-testid={`preset-${p.id}`} onClick={() => applyPreset(p.id)}
                  className={cn("text-left px-2.5 h-8 rounded-ctrl text-compact hover:bg-surface-2", value?.label === p.label && "ez-seg-active")}>{p.label}</button>
              ))}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                {["Start", "End"].map((lbl, i) => (
                  <div key={lbl} className="flex items-center gap-1.5">
                    <span className="text-micro font-mono uppercase text-ink-muted">{lbl}</span>
                    <div className={cn("h-8 px-2 grid place-items-center rounded-ctrl border text-compact font-mono", i === 0 ? "border-signal-blue" : "border-edge-subtle")}>
                      {i === 0 ? (start ? format(start, "MM/dd/yyyy") : "—") : (end ? format(end, "MM/dd/yyyy") : "—")}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-start gap-4">
                <button onClick={() => setMonth((m) => subMonths(m, 1))} data-testid="cal-prev" className="h-7 w-7 grid place-items-center rounded-ctrl hover:bg-surface-2 shrink-0"><ChevronLeft className="h-4 w-4" /></button>
                <Month month={month} start={start} end={end} onPick={pick} maxDate={maxDate} />
                <div className="hidden sm:block"><Month month={addMonths(month, 1)} start={start} end={end} onPick={pick} maxDate={maxDate} /></div>
                <button onClick={() => setMonth((m) => addMonths(m, 1))} data-testid="cal-next" className="h-7 w-7 grid place-items-center rounded-ctrl hover:bg-surface-2 shrink-0"><ChevronRight className="h-4 w-4" /></button>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={clear} data-testid="date-clear" className="h-8 px-3 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2">Clear</button>
                <button onClick={apply} disabled={!start || !end} data-testid="date-apply"
                  className={cn("h-8 px-4 rounded-ctrl text-white text-compact", start && end ? "bg-emerald-600 hover:bg-emerald-500" : "bg-emerald-600/40 cursor-not-allowed")}>Apply</button>
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
